import { Content, Lesson } from '../../types';
import { useCallback, useMemo } from 'react';
import { useCourseContent } from '../../hooks/useCourseContent';

type ContentElements = {
  contentId: string;
  lessonId?: string;
  content: Content;
  lesson?: Lesson;
}
type ContentArr = {
  [lessonId: string]: ContentElements[];
};

export const useNextContent = () => {
  const { content, currentUnit, lesson, setLesson, setContent, setSelectionLevel, isParentApp, section } = useCourseContent();

  const contentArray: ContentArr = useMemo(() => {
    const res: ContentArr = {};
    if (!currentUnit) return res;
    if (lesson) {
      (currentUnit.classPresentation || []).forEach(l => {
        if (l?.id) {
          (l?.modules || []).forEach(c => {
            if (!res[l?.id]) {
              res[l?.id] = [];
            }
            res[l?.id].push({
              contentId: c?.id || '',
              lessonId: lesson.id,
              content: c as Content,
              lesson,
            })
          })
        }
      })
    } else {
      (currentUnit?.teacherPreparation || []).forEach(c => {
        if (!res['tp']) {
          res['tp'] = [];
        }
        res['tp'].push({
          contentId: c?.id || '',
          lessonId: undefined,
          content: c as Content,
          lesson: undefined,
        })
      })
    }
    return res;
  }, [currentUnit, lesson]);

  const nextContent = useMemo(() => {
    if (!contentArray || !lesson || !content) return undefined;
    const arr = contentArray[lesson.id];
    if (!arr || !content) return undefined
    const elIndex = arr.findIndex(c => c.contentId === content.id);
    return elIndex === arr.length - 1 ? undefined : arr[elIndex].content;
  }, [contentArray, lesson, content]);

  const contentNext = useMemo(() => {
    if (!contentArray || !content) return undefined;
    if (!lesson) {
      return ({
        lessonId: undefined || "",
        moduleId: undefined || "",
        previousModuleId: undefined,
        unitId: currentUnit?.id || "",
      });
    }
    const arr = contentArray[lesson.id];
    if (!arr || !content) return undefined
    const elIndex = arr.findIndex(c => c.contentId === content.id);
    return arr[elIndex + 1] ? {
      lessonId: arr[elIndex + 1].lessonId || "",
      moduleId: arr[elIndex + 1].contentId || "",
      previousModuleId: content.id,
      unitId: currentUnit?.id || "",
      sectionId: section?.id
    } : undefined;
  }, [contentArray, lesson, content, currentUnit?.id, section?.id]);

  const hasNextContent = !!nextContent;

  const hasPrevContent = useMemo(() => {
    if (!contentArray || !lesson || !content) return false;
    const arr = contentArray[lesson.id];
    if (!arr || !content) return false
    const elIndex = arr.findIndex(c => c.contentId === content.id);
    return elIndex > 0;
  }, [contentArray, lesson, content]);

  const selectNext = useCallback((currentContent?: Content) => {
    const contentObj = currentContent || content;
    if (!contentObj || !lesson) return;
    const arr = contentArray[lesson.id];
    if (!arr) return false
    const currentIndex = arr.findIndex(c => c.contentId === contentObj.id) + 1;
    const next = arr[currentIndex];
    if (!!next) {
      setContent(next.content);
      setLesson(next.lesson);
      setSelectionLevel('Content');
    }
  }, [content, lesson, contentArray, setContent, setLesson, setSelectionLevel]);

  const selectPrev = useCallback(() => {
    if (!content || !lesson) return;
    const arr = contentArray[lesson.id];
    if (!arr || !content) return false
    const currentIndex = arr.findIndex(c => c.contentId === content.id);
    const next = arr[currentIndex - 1];
    if (next) {
      setContent(next.content);
      setLesson(next.lesson);
      setSelectionLevel('Content');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentArray, content, lesson]);

  return {
    hasNextContent,
    hasPrevContent,
    selectNext,
    selectPrev,
    nextContent,
    contentNext,
    isParentApp,
    content,
  };
}
